import { useState, useEffect, useCallback } from 'react';
import { getLeadNotes } from '../api/leadNotes';
import type { LeadNote } from '../types';

/**
 * Hook for fetching lead notes
 * @param leadId - The ID of the lead
 * @returns Notes data, loading state, error state, and refetch function
 */
export function useLeadNotes(leadId: number | null) {
  const [data, setData] = useState<LeadNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!leadId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const notes = await getLeadNotes(leadId);
      setData(notes);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch notes');
    } finally {
      setIsLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchNotes,
  };
}
